import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { ProjectStageStatus, type ProjectGovernanceDto } from '@paladin-monorepo/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDate } from '@/utils/formatDate';
import { formatPrice } from '@/utils/formatPrice';
import { projectGovernanceApi } from '@/lib/api/project-governance';
import { useGovernanceMutation } from './useGovernanceMutation';

type StageRow = { id?: string; title: string; plannedStart: string; plannedEnd: string; plannedBudget: string };

export function GovernanceStagesForm({
  projectId,
  governance,
  currency,
}: {
  projectId: string;
  governance: ProjectGovernanceDto;
  currency: string;
}) {
  const { t } = useTranslation();
  const fixed = governance.stages.filter((s) => s.status !== ProjectStageStatus.Planned);
  const planned = governance.stages.filter((s) => s.status === ProjectStageStatus.Planned);
  const canEdit = governance.permissions.isProjectManager && governance.state !== 'closed';
  const budget = (value: number | null) =>
    value === null ? null : <span className="text-xs text-muted-foreground">{formatPrice(value, currency)}</span>;

  const schema = yup
    .object({
      stages: yup
        .array(
          yup.object({
            id: yup.string().optional(),
            title: yup.string().trim().required(t('workspace.governance.stages.titleRequired')).max(255),
            plannedStart: yup.string().defined(),
            plannedEnd: yup.string().defined(),
            plannedBudget: yup
              .string()
              .defined()
              .test('budget', t('workspace.governance.stages.budgetInvalid'), (v) => !v || Number(v) >= 0),
          }),
        )
        .required(),
    })
    .required();

  const { control, register, handleSubmit, formState } = useForm<{ stages: StageRow[] }>({
    resolver: yupResolver(schema),
    values: {
      stages: planned.map((s) => ({
        id: s.id,
        title: s.title,
        plannedStart: s.plannedStart ?? '',
        plannedEnd: s.plannedEnd ?? '',
        plannedBudget: s.plannedBudget === null ? '' : String(s.plannedBudget),
      })),
    },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'stages' });

  const mutation = useGovernanceMutation(projectId, (stages: StageRow[]) =>
    projectGovernanceApi.upsertStages(projectId, {
      expectedVersion: governance.version,
      stages: stages.map((s) => ({
        id: s.id,
        title: s.title,
        plannedStart: s.plannedStart || null,
        plannedEnd: s.plannedEnd || null,
        plannedBudget: s.plannedBudget === '' ? null : Number(s.plannedBudget),
      })),
    }),
  );

  return (
    <div className="space-y-3">
      {fixed.length > 0 && (
        <ul className="space-y-1 text-sm">
          {fixed.map((s) => (
            <li key={s.id} className="flex items-center gap-2">
              <span className="font-medium">{s.title}</span>
              <Badge variant={s.status === ProjectStageStatus.Authorized ? 'success' : 'secondary'}>
                {t(`workspace.governance.stageStatus.${s.status}`)}
              </Badge>
              {s.authorizedAt && (
                <span className="text-xs text-muted-foreground">{formatDate(s.authorizedAt)}</span>
              )}
              {budget(s.plannedBudget)}
            </li>
          ))}
        </ul>
      )}

      {!canEdit ? (
        planned.length === 0 && fixed.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('workspace.governance.stages.empty')}</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {planned.map((s) => (
              <li key={s.id} className="flex items-center gap-2">
                <span>{s.title}</span>
                <Badge variant="outline">{t('workspace.governance.stageStatus.planned')}</Badge>
                {budget(s.plannedBudget)}
              </li>
            ))}
          </ul>
        )
      ) : (
        <form onSubmit={handleSubmit((v) => mutation.mutate(v.stages))} className="space-y-2">
          {fields.map((field, index) => (
            <div key={field.id} className="grid grid-cols-[1fr_auto_auto_8rem_auto] items-start gap-2">
              <div>
                <Input
                  placeholder={t('workspace.governance.stages.titlePlaceholder')}
                  {...register(`stages.${index}.title`)}
                />
                {formState.errors.stages?.[index]?.title && (
                  <p className="text-xs text-destructive">{formState.errors.stages[index]?.title?.message}</p>
                )}
              </div>
              <Input type="date" aria-label={t('workspace.governance.stages.plannedStart')} {...register(`stages.${index}.plannedStart`)} />
              <Input type="date" aria-label={t('workspace.governance.stages.plannedEnd')} {...register(`stages.${index}.plannedEnd`)} />
              <div>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder={t('workspace.governance.stages.budgetPlaceholder', { currency })}
                  aria-label={t('workspace.governance.stages.plannedBudget')}
                  {...register(`stages.${index}.plannedBudget`)}
                />
                {formState.errors.stages?.[index]?.plannedBudget && (
                  <p className="text-xs text-destructive">{formState.errors.stages[index]?.plannedBudget?.message}</p>
                )}
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={() => remove(index)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => append({ title: '', plannedStart: '', plannedEnd: '', plannedBudget: '' })}
            >
              <Plus className="mr-1 h-4 w-4" />
              {t('workspace.governance.stages.add')}
            </Button>
            <Button type="submit" size="sm" disabled={mutation.isPending}>
              {t('workspace.governance.stages.save')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
